import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { getMe } from './api/trainer';
import { TrainerMe } from './api/types';
import { errorMessage, isForbidden, isUnauthorized } from './api/errors';
import { onSessionProblem } from './api/client';
import AppNav from './components/AppNav';
import ForbiddenScreen from './components/ForbiddenScreen';
import LoginScreen from './components/LoginScreen';
import LoginSuccessPage from './components/LoginSuccessPage';
import StatusMessage from './components/StatusMessage';
import ActivitiesPage from './pages/ActivitiesPage';
import ActivityDetailPage from './pages/ActivityDetailPage';
import DashboardPage from './pages/DashboardPage';
import FitnessPage from './pages/FitnessPage';
import HealthPage from './pages/HealthPage';
import MetricHistoryPage from './pages/MetricHistoryPage';
import RecordEffortsPage from './pages/RecordEffortsPage';
import RecordsPage from './pages/RecordsPage';
import RecordsProgressPage from './pages/RecordsProgressPage';
import SettingsPage from './pages/SettingsPage';
import VolumePage from './pages/VolumePage';
import { useTheme } from './theme';
import { isDemoMode } from './utils/demo';

type AuthState =
  | { status: 'checking' }
  | { status: 'anonymous'; notice?: string }
  | { status: 'forbidden' }
  | { status: 'error'; message: string }
  | { status: 'signed-in'; user: TrainerMe };

function App() {
  const [theme, toggleTheme] = useTheme();

  return (
    <Routes>
      <Route path="/login-success" element={<LoginSuccessPage />} />
      <Route path="/*" element={<AuthGate theme={theme} onToggleTheme={toggleTheme} />} />
    </Routes>
  );
}

function AuthGate({ theme, onToggleTheme }: { theme: 'light' | 'dark'; onToggleTheme: () => void }) {
  const [auth, setAuth] = useState<AuthState>({ status: 'checking' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setAuth({ status: 'checking' });
    getMe()
      .then(user => {
        if (active) setAuth({ status: 'signed-in', user });
      })
      .catch(err => {
        if (!active) return;
        if (isUnauthorized(err)) {
          setAuth({ status: 'anonymous' });
        } else if (isForbidden(err)) {
          setAuth({ status: 'forbidden' });
        } else {
          setAuth({ status: 'error', message: errorMessage(err) });
        }
      });
    return () => {
      active = false;
    };
  }, [attempt]);

  // Any trainer request can find the session gone (401) or without trainer access (403).
  useEffect(
    () =>
      onSessionProblem(problem => {
        setAuth(
          problem === 'unauthorized'
            ? { status: 'anonymous', notice: 'Your session has expired. Sign in again to continue.' }
            : { status: 'forbidden' }
        );
      }),
    []
  );

  if (auth.status === 'checking') {
    return (
      <div className="login-hero">
        <StatusMessage loading error={null} />
      </div>
    );
  }
  if (auth.status === 'anonymous') return <LoginScreen notice={auth.notice} />;
  if (auth.status === 'forbidden') return <ForbiddenScreen />;
  if (auth.status === 'error') {
    return (
      <div className="login-hero">
        <div className="surface login-card">
          <StatusMessage loading={false} error={auth.message} onRetry={() => setAttempt(a => a + 1)} />
          <Button variant="link" onClick={() => setAttempt(a => a + 1)}>
            Try again
          </Button>
        </div>
      </div>
    );
  }
  return <SignedIn user={auth.user} theme={theme} onToggleTheme={onToggleTheme} />;
}

function SignedIn({ user, theme, onToggleTheme }: { user: TrainerMe; theme: 'light' | 'dark'; onToggleTheme: () => void }) {
  return (
    <>
      {isDemoMode() && <div className="demo-banner">Demo mode: sample data, nothing is saved. Add ?demo=off to leave.</div>}
      <AppNav user={user} theme={theme} onToggleTheme={onToggleTheme} />
      <main className="app-main">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/activities" element={<ActivitiesPage />} />
          <Route path="/activities/:id" element={<ActivityDetailPage />} />
          <Route path="/health" element={<HealthPage />} />
          <Route path="/health/:metric" element={<MetricHistoryPage />} />
          <Route path="/fitness" element={<FitnessPage />} />
          <Route path="/records" element={<RecordsPage />} />
          <Route path="/records/:distance" element={<RecordEffortsPage />} />
          <Route path="/volume" element={<VolumePage />} />
          <Route path="/records/progress" element={<RecordsProgressPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </>
  );
}

export default App;
